import processCustomParameters from "../Main/storage/utils/process-custom-parameters";
import setBounds from "../Main/store/utils/set-bounds";
import getNumberValueAndUnit from "./get-number-value-and-unit";
import getTargetElement from "./get-target-element";
import { G_targetElements } from "./globals/globals";
import processFunction from "./process-function";
import processNumber from "./process-number";

/**
 * Get value and unit from string value
 * @param {String} input Input string
 * @param {StorageItemProps} item
 * @param {MainClassProps | null} main
 * @returns {PropertyValueProps}
 */
const getValueAndUnit = (input, item, main) => {
  /** @type {PropertyValueProps} */
  const map = new Map([
    ["is-number", false],
    ["is-string", false],
    ["is-variable", false],
    ["is-reference", false],
    ["is-composition", false],
    ["is-no-process", false],
    ["is-inherit", false],
    ["is-fn", false],
    ["is-null", false],
    ["value", null],
    ["values", null],
    ["unit", null],
    ["composition", null],
  ]);

  const isFn = /\)$/.test(input);
  const isComposition = /^◖/.test(input);
  const isNoneInitial = item.get("is-none-initial") || false;

  if (input.includes("¦")) {
    input = input.replace(/¦/g, ",");
  }

  /**
   * Future: no-process operator `^`
   * if (input.startsWith("^")) {
      input = input.slice(1);
      map.set("is-no-process", true);
    }
   */

  map.set("output", input);
  map.set("original", input);
  map.set("is-none-initial", isNoneInitial);

  /**
   * Is function and composition
   * @example `(+)calc(100% - 3rem)`
   */
  if (isFn && isComposition && main) {
    const map = processFunction(input, item, main);
    map.set("composition", input.slice(1, 2));

    return map;
  }

  /**
   * Is composition
   * @example `◖+◗10px` original as `(+)10px`
   */
  if (isComposition) {
    const value = input.slice(3);

    map.set("is-composition", true);
    map.set("composition", input.slice(1, 2));
    map.set("output", value);

    return processNumber(value, map);
  }

  /**
   * Target
   * @example hover(target: #nav)
   */
  if (item.get("is-target")) {
    const replaced = input.replace(/▂/g, " ");
    const elements = /** @type {Array<TorusElement>} */ (
      getTargetElement(replaced, main)
    );

    map.set("value", elements);

    for (const element of elements) {
      if (!element.Torus) {
        element.Torus = {};
      }

      if (!element.Torus.Target) {
        element.Torus.Target = new Map();
      }

      if (item.get("use-bounds")) {
        element["tor-bounds"] = /** @type {BoundsProps} */ (
          setBounds(element, null, null, null, true)
        );
      }

      const target = /** @type {Map<any, any>} */ (element.Torus.Target);

      if (!target.get(main?.element)) {
        target.set(main?.element, main);
      }

      G_targetElements.add(element);
    }

    return map;
  }

  /**
   * Function
   */
  if (isFn && main) {
    return processFunction(input, item, main) || map;
  }

  /**
   * Number
   * @example `10px`
   * ^[-.]?[0-9]+(?:\.[0-9]+)?[a-zA-Z]*(%?)$
   */
  if (/^[-.]?[:\.0-9]+[a-z]*(%?)$/.test(input)) {
    const data = processNumber(input, map);
    const useBounds = item.get("use-bounds");

    if (useBounds) {
      processCustomParameters(data, item);
    }

    return getNumberValueAndUnit(data);
  }

  /**
   * Variable
   * @example `$scale`
   */
  if (/^\$/.test(input)) {
    map.set("is-variable", true);
    map.set("value", input);

    return map;
  }

  /**
   * Inheritance
   * @example `rgba(?, ?, ?, 0.3)`
   */
  if (/^\?/.test(input)) {
    map.set("is-inherit", true);
  }

  if (input === "infinite") {
    map.set("is-number", true);
    map.set("is-string", false);
    map.set("value", 1e12);

    return map;
  }

  if (item.get("name") === "name") {
    input = input.replace(/▁/g, " ");
  }

  /**
   * String
   * @example `top`
   */
  map.set("is-string", true);
  map.set("value", input);

  /**
   * Calc `calc(100▄-▄3rem)`
   */
  if (input.includes("▄") && main) {
    return processFunction(`calc(${input})`, item, main);
  }

  if (input === "null") {
    map.set("is-null", true);
    map.set("output", null);
  }

  if (input === "true") {
    map.set("output", true);
    map.set("value", true);
  }

  if (input === "false") {
    map.set("output", false);
    map.set("value", false);
  }

  return map;
};

export default getValueAndUnit;
